import type {
	DeliveryConfigResponse,
	RunCreated,
	RunDetail,
	RunEventListResponse,
	RunListResponse,
	RunResumed,
	RunStatus,
	SessionCreated,
	SessionDetail,
	SessionListResponse,
} from '@/entities/sender.entity';
import { senderApi } from './sender-http';

export type {
	DeliveryConfigResponse,
	DeliveryProfile,
	RunCreated,
	RunDetail,
	RunEvent,
	RunEventListResponse,
	RunListResponse,
	RunPhase,
	RunResumed,
	RunStatus,
	RunSummary,
	SessionCreated,
	SessionDetail,
	SessionListResponse,
	SessionSummary,
	SessionStatus,
} from '@/entities/sender.entity';

export type BuildSource = 'panel' | 'upload';

export interface ISenderBuild {
	id: string;
	buildId: string;
	profileId: number;
	source: BuildSource;
	appName: string;
	sizeBytes: number;
	createdAt: string;
}

export interface IBuildListResponse {
	items: ISenderBuild[];
	nextCursor?: string | null;
}

export interface IListParams {
	limit?: number;
	cursor?: string;
}

export interface IStartRunInput {
	session?: File;
	sessionId?: string;
	caption: string;
	sessionSidecar?: File;
	buildId?: string;
	senderBuildId?: string;
	sessionLabel?: string;
}

export interface ICreateSessionInput {
	phone: string;
	label?: string;
}

export interface IRunCancelled {
	id: string;
	status: 'cancelled';
}

const APK_UPLOAD_TIMEOUT_MS = 300_000;
const SESSION_UPLOAD_TIMEOUT_MS = 120_000;

export async function fetchDeliveryConfig() {
	const { data } = await senderApi.get<DeliveryConfigResponse>('/delivery-config');
	return data;
}

export async function fetchRuns(params?: IListParams & { status?: RunStatus }) {
	const { data } = await senderApi.get<RunListResponse>('/runs', { params });
	return data;
}

export async function fetchRun(runId: string) {
	const { data } = await senderApi.get<RunDetail>(`/runs/${runId}`);
	return data;
}

export async function fetchRunEvents(runId: string, params?: IListParams) {
	const { data } = await senderApi.get<RunEventListResponse>(`/runs/${runId}/events`, { params });
	return data;
}

export async function createRun(input: IStartRunInput) {
	const form = new FormData();
	if (input.session) {
		form.append('session', input.session);
	}
	if (input.sessionId) {
		form.append('sessionId', input.sessionId);
	}
	form.append('caption', input.caption.trim());
	if (input.sessionSidecar) {
		form.append('sessionSidecar', input.sessionSidecar);
	}
	if (input.buildId?.trim()) {
		form.append('buildId', input.buildId.trim());
	}
	if (input.senderBuildId) {
		form.append('senderBuildId', input.senderBuildId);
	}
	if (input.sessionLabel?.trim()) {
		form.append('sessionLabel', input.sessionLabel.trim());
	}
	const { data } = await senderApi.post<RunCreated>('/runs', form, {
		timeout: SESSION_UPLOAD_TIMEOUT_MS,
	});
	return data;
}

export async function fetchSessions() {
	const { data } = await senderApi.get<SessionListResponse>('/sessions', {
		params: { status_filter: 'ready' },
	});
	return data;
}

export async function createSession(input: ICreateSessionInput) {
	const { data } = await senderApi.post<SessionCreated>('/sessions', input, {
		suppressToast: true,
	});
	return data;
}

export async function confirmSession(sessionId: string, code: string) {
	const { data } = await senderApi.post<SessionDetail>(`/sessions/${sessionId}/confirm`, { code }, {
		suppressToast: true,
	});
	return data;
}

export async function deleteSession(sessionId: string) {
	await senderApi.delete(`/sessions/${sessionId}`, { suppressToast: true });
}

export async function cancelRun(runId: string) {
	const { data } = await senderApi.post<IRunCancelled>(`/runs/${runId}/cancel`);
	return data;
}

export async function resumeRun(runId: string) {
	const { data } = await senderApi.post<RunResumed>(`/runs/${runId}/resume`);
	return data;
}

export async function fetchBuilds(params?: IListParams) {
	const { data } = await senderApi.get<IBuildListResponse>('/builds', { params });
	return data;
}

export async function uploadBuild(file: File, appName?: string) {
	const form = new FormData();
	form.append('file', file);
	if (appName?.trim()) {
		form.append('appName', appName.trim());
	}
	const { data } = await senderApi.post<ISenderBuild>('/builds', form, {
		timeout: APK_UPLOAD_TIMEOUT_MS,
	});
	return data;
}
